"use client";

import Image from "next/image";
import { L as Link, useDesign } from "@/components/design";
import { useState } from "react";
import { catLabel, cutout, rub, type Product } from "@/lib/catalog";
import { cart } from "../cart";
import { compare, favs } from "./sets";
import { openQuickView } from "./ShopLayer";
import Stars from "./Stars";

// Варианты в карточке: короткие (размеры, объёмы) — кнопками, длинные — списком, один — подписью.
export function VariantPick({ p, value, onChange, id }: { p: Product; value: number; onChange: (i: number) => void; id: string }) {
  if (p.variants.length === 1) return <p className="vpick vpick--one">{p.variants[0].name === "Стандарт" ? "Один вариант" : p.variants[0].name}</p>;
  const short = p.variants.length <= 5 && p.variants.every((v) => v.name.length <= 6);
  if (short)
    return (
      <div className="vpick vpick--chips" role="radiogroup" aria-label={p.variantLabel ?? "Вариант"}>
        {p.variants.map((v, i) => (
          <button key={v.name} type="button" role="radio" aria-checked={value === i} onClick={() => onChange(i)}>
            {v.name}
          </button>
        ))}
      </div>
    );
  return (
    <div className="vpick">
      <label className="sr" htmlFor={id}>
        {p.variantLabel ?? "Вариант"}
      </label>
      <select id={id} value={value} onChange={(e) => onChange(Number(e.target.value))}>
        {p.variants.map((v, i) => (
          <option key={v.name} value={i}>
            {v.name} — {rub(v.price)}
          </option>
        ))}
      </select>
    </div>
  );
}

export default function ProductCard({ p }: { p: Product }) {
  const [vi, setVi] = useState(0);
  const [added, setAdded] = useState(false);
  const fav = favs.use().includes(p.slug);
  const cmp = compare.use().includes(p.slug);
  const v = p.variants[vi];
  const href = `/catalog/${p.slug}`;
  const { design } = useDesign();
  const main = design === "gallery" ? cutout(p) : p.images[0].src;
  const alt = design === "gallery" ? p.images[0].src : p.images[1]?.src;
  const sale = p.variants.find((x) => x.oldPrice);

  return (
    <article className="card">
      <div className="card__media">
        <Link href={href} tabIndex={-1} aria-hidden="true">
          <Image src={main} alt="" width={900} height={900} sizes="(max-width: 700px) 50vw, (max-width: 1200px) 33vw, 20vw" />
          {alt && <Image className="card__alt" src={alt} alt="" width={900} height={900} sizes="(max-width: 700px) 50vw, (max-width: 1200px) 33vw, 20vw" />}
        </Link>
        <span className="card__badges">
          {p.badge && <span className={`badge label${p.badge.tone === "new" ? " new" : ""}`}>{p.badge.text}</span>}
          {sale?.oldPrice && <span className="badge badge--sale label">−{Math.round((1 - sale.price / sale.oldPrice) * 100)}%</span>}
        </span>
        <button type="button" className="card__fav" aria-pressed={fav} aria-label={fav ? "Убрать из избранного" : "В избранное"} onClick={() => favs.toggle(p.slug)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />
          </svg>
        </button>
        <button type="button" className="card__cmp" aria-pressed={cmp} aria-label={cmp ? "Убрать из сравнения" : "Добавить к сравнению"} title="Сравнить" onClick={() => compare.toggle(p.slug)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 20V10M12 20V4M18 20v-7" />
          </svg>
        </button>
        <button type="button" className="card__quick label" onClick={() => openQuickView(p.slug)}>
          Быстрый просмотр
        </button>
      </div>
      <div className="body">
        <p className="card__meta">
          <span className="card__cat label">{catLabel(p.category)}</span>
          <span className="card__rate">
            <Stars value={p.rating} /> <span>{p.rating.toFixed(1).replace(".", ",")}</span> <small>· {p.ratingCount}</small>
          </span>
        </p>
        <h3>
          <Link href={href}>{p.name}</Link>
        </h3>
        <VariantPick p={p} value={vi} onChange={(i) => (setVi(i), setAdded(false))} id={`v-${p.slug}`} />
        <div className="buy">
          <span className="price">
            {rub(v.price)}
            {v.oldPrice && <s>{rub(v.oldPrice)}</s>}
          </span>
          <button
            type="button"
            className={`add label${added ? " done" : ""}`}
            onClick={() => {
              cart.add(p.slug, v.name);
              setAdded(true);
            }}
          >
            {added ? "Добавлено" : "В корзину"}
          </button>
        </div>
      </div>
    </article>
  );
}
